/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Spin, Empty } from 'antd';
import { useIntl } from 'react-intl';
import debounce from 'lodash/debounce';
import {
  getOfferFinalizationPreview,
  getOfferReopenPreview,
} from '~src/services/api/notifications';

interface OfferNotificationPreviewProps {
  title: string;
  description: string;
  type: 'finalize' | 'reopen';
  projectId: number;
  tenderId: number;
}

export const OfferNotificationPreview: React.FC<OfferNotificationPreviewProps> = ({
  title,
  description,
  type,
  projectId,
  tenderId,
}) => {
  const intl = useIntl();
  const [htmlContent, setHtmlContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Debounced preview fetch
  const fetchPreview = useCallback(
    debounce(async (t: string, d: string) => {
      if (!t && !d) {
        setHtmlContent('');
        return;
      }

      try {
        setIsLoading(true);
        // Move API function selection inside to avoid stale closure
        const isFinalize = type === 'finalize';
        const getPreviewFn = isFinalize
          ? getOfferFinalizationPreview
          : getOfferReopenPreview;

        const html = await getPreviewFn({
          projectId,
          tenderId,
          title: t,
          description: d,
        });
        setHtmlContent(html);
      } catch (error) {
        console.error('Failed to fetch preview:', error);
        // Don't clear htmlContent - keep last successful preview
      } finally {
        setIsLoading(false);
      }
    }, 500),
    [projectId, tenderId, type]
  );

  useEffect(() => {
    fetchPreview(title, description);
  }, [title, description, fetchPreview]);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: '#fafafa',
        border: '1px solid #e8e8e8',
        borderRadius: 6,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 16 }}>
        {intl.formatMessage({ id: 'offer-notification-preview-title' })}
      </Typography.Title>

      {isLoading && (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'white',
            borderRadius: 4,
          }}>
          <Spin />
        </div>
      )}

      {!isLoading && !htmlContent && (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'white',
            borderRadius: 4,
          }}>
          <Empty
            description={intl.formatMessage({
              id: 'offer-notification-preview-empty',
            })}
          />
        </div>
      )}

      {!isLoading && htmlContent && (
        <div
          style={{
            flex: 1,
            border: '1px solid #d9d9d9',
            borderRadius: 4,
            background: 'white',
            overflow: 'hidden',
            display: 'flex',
          }}>
          <iframe
            srcDoc={htmlContent}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block',
            }}
            sandbox="allow-same-origin"
            title="Email Preview"
          />
        </div>
      )}
    </div>
  );
};
